import { srmModuleReportData } from '../../functions/src/common/srmModuleReports';
import { cleanReportPromptText } from '../../functions/src/common/reportPromptText';
import { getEventRiskScore } from '../utils/riskEventFeed';
import { reportSaveScope, type ReportSaveScope } from './analysisPersistence';

const RECORD_FIELDS = new Set(['id', 'reportId', 'sourceReportId', 'reportIdentity', 'title', 'reportTitle', 'type',
    'reportType', 'savedAt', 'timestamp', 'userId', 'userEmail', 'userName', 'organizationId', 'clientId', 'assetId',
    'srmProjectId', 'srmModuleId', 'workflowId', 'mappedTo', 'isMapping', 'deliverySnapshot', 'serviceRequestId',
    'consultantReportDelivery', 'serviceName', 'deliveryKind', 'deliveredBy', 'deliveredByUid', 'reviewedAt', 'inputs']);

function canonical(value: any): any {
    if (Array.isArray(value))
        return value.map(canonical);
    if (value && typeof value === 'object') {
        const result: Record<string, any> = {};
        for (const key of Object.keys(value).sort())
            if (value[key] !== undefined)
                result[key] = canonical(value[key]);
        return result;
    }
    return typeof value === 'string' ? value.replace(/\r\n/g, '\n').trim() : value;
}

export function reportContentKey(value: any): string {
    const report = cleanReportPromptText(srmModuleReportData(value));
    if (!report || typeof report !== 'object' || report.error)
        return '';
    const body: Record<string, any> = {};
    for (const key of Object.keys(report))
        if (!RECORD_FIELDS.has(key) && key !== 'additionalSections')
            body[key] = report[key];
    body.summary = report.summary || report.advisorySummary || report.executiveSummary || report.tacticalSummary
        || report.reportSummary || report.riskSummary || '';
    if (!report.reportArea)
        body.riskScore = getEventRiskScore(report) ?? report.riskScore ?? report.overallGeopoliticalRiskScore;
    const additional = (Array.isArray(report.additionalSections) ? report.additionalSections : []).filter((section: any) => {
        if ((Array.isArray(report.sections) ? report.sections : []).some((original: any) => canonical(
            typeof original.content === 'string' ? original.content : JSON.stringify(original.content, null, 2)) === canonical(section.content)
            && (original.title || original.heading) === (section.title || section.heading)))
            return false;
        return !Object.entries(report).some(([key, content]) => typeof content === 'string' && content === section.content
            && section.title === key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().replace(/^\w/, character => character.toUpperCase()));
    });
    if (additional.length)
        body.additionalSections = additional;
    return JSON.stringify(canonical(body));
}

export function reportMatchesScope(value: any, scope: ReportSaveScope): boolean {
    const candidate = reportSaveScope({ ...value, ...(value?.resultData || value?.reportData) });
    if (candidate.organizationId && candidate.organizationId !== scope.organizationId
        || (candidate.clientId || null) !== (scope.clientId || null))
        return false;
    if (candidate.assetId && scope.assetId)
        return candidate.assetId === scope.assetId;
    if (candidate.srmProjectId && scope.srmProjectId)
        return candidate.srmProjectId === scope.srmProjectId;
    return !candidate.assetId && !candidate.srmProjectId;
}

export async function libraryReportId(content: string, scope: ReportSaveScope): Promise<string> {
    const identity = JSON.stringify([scope.organizationId, scope.clientId || '', scope.assetId || scope.srmProjectId || '', content]);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity));
    return `report-${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;
}
